/** Вкладка «По дням»: месяц площадки (юрлицо — из шапки) по дням — заказы (всего / FBO / FBS),
 *  выкуп, все удержания P&L, себестоимость, расходы, ДРР, к перечислению, маржа; итоги недель с Δ и итог
 *  месяца. Период и сравнение шапки здесь не действуют — свой месяц (`month` в адресе). Числа —
 *  `GET /mp-analytics/{mp}/daily`; источник правды — docs/ozon-analytics.md «По дням». */
import React, { Component, useState } from "react";

import type { DailyChannel, DailySpan, MpDaily } from "@/api/mpAnalytics";
import { cn } from "@/lib/utils";
import { cogsNullTitle } from "./cogs";
import { DailyMatrix } from "./DailyMatrix";
import { fmtBuiltMsk, fmtDate, fmtWhen, int, martStaleWarning, monthIn, rub } from "./format";
import { useMp } from "./mp";
import { MonthPicker } from "./MonthPicker";
import { useDaily } from "./queries";
import { ErrorBox, Loading, Seg } from "./shared";

const CHANNELS: { id: DailyChannel; label: string }[] = [
  { id: "all", label: "Всего" },
  { id: "fbo", label: "FBO" },
  { id: "fbs", label: "FBS" },
];

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const CHIP = "rounded-full border px-2 py-0.5 text-muted-foreground";
const WARN =
  "rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300";

export function DailyTab({
  month,
  today,
  legal,
  channel,
  onMonth,
  onChannel,
}: {
  month: string;
  today: string;
  legal: string | null;
  channel: DailyChannel;
  onMonth: (m: string) => void;
  onChannel: (c: DailyChannel) => void;
}): React.ReactElement {
  const mp = useMp();
  const q = useDaily(mp.id, month, legal, channel);
  // раскрытые группы — здесь, а не в таблице: смена канала / месяца / юрлица (новый запрос, таблица
  // на время загрузки пропадает) не сворачивает то, что пользователь раскрыл
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // ошибка фонового перезапроса при старых данных: показываем и ошибку, и старую таблицу —
  // помеченной «могли устареть», а не как текущую
  const stale = q.isError && !!q.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <MonthPicker month={month} today={today} onMonth={onMonth} label="Месяц отчёта по дням" testId="daily-month" />
        <Seg label="Заказы" value={channel} options={CHANNELS} onChange={onChannel} />
      </div>
      {q.isLoading && <Loading text="Считаю матрицу по дням…" />}
      {q.isError && <ErrorBox error={q.error} what="отчёт по дням" />}
      {q.data && (
        <div
          className={cn("space-y-3", stale && "opacity-60")}
          data-testid="daily-body"
          data-stale={stale || undefined}
        >
          {stale && (
            <div role="alert" className={cn(WARN, "inline-block rounded-md")} data-testid="daily-stale">
              Показан прошлый ответ — данные могли устареть: обновить не удалось (ошибка выше).
            </div>
          )}
          <DailyRenderBoundary key={q.dataUpdatedAt}>
            <DailyStatus data={q.data} mpLabel={mp.label} />
            <DailyMatrix data={q.data} open={open} setOpen={setOpen} />
          </DailyRenderBoundary>
          <p className="text-xs text-muted-foreground">
            Пустая ячейка — данных за день ещё нет (не 0): «данные по» — последний день, полный у всех
            действующих кабинетов (по свежести загрузок). Неделя — Пн–Вс, неделя 1 — с понедельника,
            даже если он в прошлом месяце; итог месяца — только его дни. Δ дн% — последний день недели
            с данными (у текущей — последний полный) к предыдущему; Δ нед% — к прошлой неделе, у
            неполной недели — к тем же дням прошлой недели. Выкуплено − Расходы = Маржа; расходы — все
            удержания и себестоимость; «К перечислению» — выкуплено − удержания (без себестоимости).
            Удержания, расходы, отмены и себестоимость — без минуса; «—» — себестоимости за день ещё
            нет (считается с даты начала). Клик по строке P&L — график за месяц.
          </p>
        </div>
      )}
    </div>
  );
}

/** Ответ бэка разошёлся с контрактом (ячейки, окно графика) — текст ошибки вместо таблицы, а не
 *  упавшая страница или пустые ячейки. Новый ответ (`key`) — сброс. */
class DailyRenderBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("«По дням»: ошибка отображения", error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          data-testid="daily-render-error"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          Не удалось показать отчёт по дням: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

/** «ограничивает: ИП X — лента заказов (загрузка) 30.09.2026 09:00» — кто держит «данные по». */
function spanLimitText(span: DailySpan): string | null {
  const l = span.limit;
  if (!l) return null;
  if (l.legal === null) return `ограничивает: ${l.label}`;
  const at = l.value && l.value !== l.through ? ` ${fmtWhen(l.value)}` : "";
  return `ограничивает: ${l.legal} — ${l.label}${at}`;
}

/** Сроки всех кабинетов — подсказка к «данные по». */
function spanTitle(span: DailySpan): string {
  return span.cabinets
    .map((c) => {
      const what = c.through ? `по ${fmtDate(c.through)}` : "нет данных";
      const note = !c.active ? " (закрыт — срок не держит)" : !c.holds ? " (нет данных в окне — срок не держит)" : "";
      const src = c.limit ? ` — ${c.limit.label}` : "";
      return `${c.legal}: ${what}${src}${note}`;
    })
    .join("\n");
}

function Fresh({
  what,
  builtAt,
  span,
  testId,
}: {
  what: string;
  builtAt: string;
  span: DailySpan;
  testId: string;
}): React.ReactElement {
  const limit = spanLimitText(span);
  return (
    <span className={CHIP} data-testid={testId} title={spanTitle(span) || undefined}>
      {what}: витрина собрана {fmtBuiltMsk(builtAt)} · данные по {fmtDate(span.to)}
      {limit && (
        <span className="text-foreground/70" data-testid={`${testId}-limit`}>
          {" "}
          · {limit}
        </span>
      )}
    </span>
  );
}

/** Свежесть обеих витрин, подсказки «канал не определён», «кабинет закрыт», себестоимость. */
function DailyStatus({ data, mpLabel }: { data: MpDaily; mpLabel: string }): React.ReactElement {
  const legalOrders = data.legal ? data.orders.data_through[data.legal] : undefined;
  const closed = legalOrders?.active === false;
  const stale = [martStaleWarning(data.orders.built_at), martStaleWarning(data.mart.built_at)];
  const cogsMissing = !data.cogs.covered_cur;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="daily-status">
      <Fresh what="Заказы" builtAt={data.orders.built_at} span={data.spans.orders} testId="daily-fresh-orders" />
      <Fresh what="Финансы" builtAt={data.mart.built_at} span={data.spans.finance} testId="daily-fresh-finance" />
      {stale[0] && <span role="alert" className={WARN}>Заказы: {stale[0]}</span>}
      {stale[1] && <span role="alert" className={WARN}>Финансы: {stale[1]}</span>}
      {closed && <span className={WARN}>кабинет закрыт — заказы по {fmtDate(data.spans.orders.to)}</span>}
      {data.null_channel && (
        <span className={WARN} data-testid="daily-null-channel">
          {capitalize(monthIn(data.month))} есть заказы {mpLabel} без канала (до 01.06.2026):{" "}
          {int(data.null_channel.qty)} шт., {rub(data.null_channel.amount)} — входят только во «Всего»
          {data.channel !== "all" && `, в ${data.channel.toUpperCase()} их нет`}
        </span>
      )}
      {cogsMissing && (
        <span className={WARN} data-testid="daily-cogs">
          Себестоимость, расходы и маржа: {cogsNullTitle(data.cogs, mpLabel)}
        </span>
      )}
    </div>
  );
}
